import keycloakInstance from "@/initKeycloak";
import axios from "axios";
import * as Sentry from "@sentry/react";
import { attachRetryOn401 } from "@/shared/api/interceptors/retryOn401";
import { SubscriptionsResponse } from "../types";

import { client as prefServiceClient } from "@/shared/api/pref-storage";
import { MailingListSubscription } from "./types";

const baseURL =
  import.meta.env.VITE_MAILING_LISTS_API_URL || "/api/mailing-lists";

export const client = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
  },
});

client.interceptors.request.use(async (config) => {
  if (keycloakInstance && keycloakInstance.authenticated) {
    try {
      await keycloakInstance.updateToken(30); // Refresh if expiring within 30s
      config.headers.Authorization = `Bearer ${keycloakInstance.token}`;
    } catch (error) {
      console.error("Token refresh error in interceptor:", error);
    }
  }
  return config;
});

client.interceptors.response.use(
  (response) => response,
  (error) => {
    Sentry.captureException(error, {
      extra: {
        axiosDetails: {
          msg: error.message,
          code: error.code,
          config: error.config,
          response: error.response,
          data: error.response?.data,
        },
      },
    });
    return Promise.reject(error);
  }
);

attachRetryOn401(client);

export const getMailingLists = async (): Promise<unknown[]> => {
  const response = await prefServiceClient.get("/mailing-lists");
  return response.data;
};

export const subscribeToMailingList = async (
  mailingListId: string
): Promise<MailingListSubscription> => {
  const response = await prefServiceClient.post("/mailing-list-subscriptions", {
    mailing_list_id: mailingListId,
  });
  return response.data;
};

export const unsubscribeFromMailingList = async (id: string): Promise<void> => {
  await prefServiceClient.delete(`/mailing-list-subscriptions/${id}`);
};

/**
 * GET /subscriptions
 * Fetches all subscription statuses for the current user.
 * Auto-subscribes new users to defaults on first load (backend behavior).
 */
export const getSubscriptions = async (): Promise<SubscriptionsResponse> => {
  const response = await client.get("/subscriptions");
  return response.data;
};

/**
 * PUT /subscriptions/{audienceKey}
 * Subscribes the user to a mailing list.
 */
export const subscribe = async (audienceKey: string): Promise<void> => {
  await client.put(`/subscriptions/${audienceKey}`);
};

/**
 * DELETE /subscriptions/{audienceKey}
 * Unsubscribes the user from a mailing list.
 */
export const unsubscribe = async (audienceKey: string): Promise<void> => {
  await client.delete(`/subscriptions/${audienceKey}`);
};
